<template>
    <div class="Mcp-TunnelSimulatorPage">

        <main class="TunnelSimulatorPage__content">
            
            <!-- Left -- image banner -->
            <div class="ImageBanner-container">
                <picture>
                    <source
                        media="(max-width: 768px)"
                        :data-srcset="`${mediaBaseUrl}simulator/img_mobile-sidebar.svg`">
                     <source
                        media="(min-width: 769px)"
                        :data-srcset="`${mediaBaseUrl}simulator/img-sidebar.jpg`">
                    <img
                        src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
                        :data-src="`${mediaBaseUrl}simulator/img-sidebar.jpg`"
                        class="lazyload"
                        alt=""/>
                </picture>
            </div>

            <!-- Right -->
            <div class="FormQuestions-container">
                <!-- Tab content -->
                <TabSteps/>
                
                <div class="FormQuestions-container__wrapper">
                    <!-- Main content -->
                    <div 
                        v-if="currentQuestion.index <= (currentForm.questions.length-1)  && !leadForm.display"
                        class="FormQuestions-container__question-container"
                    >
                            <template v-for="(question, index) in currentForm.questions" >
                                
                                <CheckboxQuestion 
                                    v-if="question.input_type === 'radio' && index === currentQuestion.index"
                                    :questionDatas="question"
                                    :mediaBaseUrl="mediaBaseUrl"
                                    :currentAnswer="_getCurrentAnswer()"
                                    :displayErrorMessage="displayErrorMessage"
                                    :blogAnswerUrlParams="blogAnswerUrlParams"
                                    @Answer::choice="handleAnswer"
                                    @Answer::sub-question="_setSubQuestion"
                                    @Remove::sub-question="_removeSubQuestion"
                                    :key="index"
                                />

                                <InputQuestion 
                                    v-if="question.input_type === 'number' && index === currentQuestion.index"
                                    :questionDatas="question"
                                    :inputType="question.input_type"
                                    :currentAnswer="_getCurrentAnswer()"
                                    :displayErrorMessage="displayErrorMessage"
                                    @Answer::choice="handleAnswer"
                                    @Answer::invalid="setAnswerValidity(false)"
                                    @InputOption::continue-tunnel="continueTunnelForm"
                                    :key="index"
                                />

                                <RangeQuestion 
                                    v-if="question.input_type === 'range' && index === currentQuestion.index"
                                    :questionDatas="question"
                                    :currentAnswer="_getCurrentAnswer()"
                                    :markers="['0', '100', '200', '300', '400']"
                                    @Answer::choice="handleAnswer"
                                    :key="index"
                                />  
                            </template>

                            <!-- Cta content -->
                            <ActionsButtons 
                                :currentQuestionIndex="currentQuestion.index"
                                :displaySecondBtn="currentIndexQuestion > 0"
                                @primaryBtn::action="nextQuestion()"
                                @secondBtn::action="prevQuestion()"
                            />

                            <div
                                v-if="currentForm.questions[currentQuestion.index].info"
                                class="FormQuestions-container__question-info"
                            >
                                <svg v-svg symbol="Info" size="0 0 15 15" role="presentation" class="icon--inline"></svg>
                                <p>{{currentForm.questions[currentQuestion.index].info}}</p>
                            </div>
                    </div>

                    <LeadForm 
                        v-if="leadForm.display"
                        @LeadForm::backToTunnel="hideLeadForm"
                        @LeadForm::scrollToError="_scrollToErrorMessage()"
                        @LeadForm::dpeEligibility="handleOverallRenovationQuestion"
                        :formAnswer="leadFormAnswers"
                    />
                    

                </div>
            </div>
        </main>
    </div>
</template>

<script>
import CheckboxQuestion from '@/includes/Simulator/FormComponents/CheckboxQuestion.vue';
import InputQuestion from '@/includes/Simulator/FormComponents/InputQuestion.vue';
import RangeQuestion from '@/includes/Simulator/FormComponents/RangeQuestion.vue';
import LeadForm from '@/includes/Simulator/FormComponents/LeadForm';
import ActionsButtons from '@/includes/Simulator/ActionsComponents/ActionsButtons.vue';
import TabSteps from '@/includes/Simulator/ActionsComponents/TabSteps.vue';

import tunnelFormMixin from "@/mixins/actions/simulator/tunnelFormMixin";

import EventBus from "@/event-bus";


export default {
    name: "TunnelSimulatorPage",

    /* All forms, questions, answers stuff in mixin */
    mixins: [tunnelFormMixin],

    components: {
        CheckboxQuestion,
        InputQuestion,
        RangeQuestion,
        LeadForm,
        ActionsButtons,
        TabSteps,
    },

    props: {
        tunnelForm: {
            type: Object,
            default: {},
        },
        isLeadForm: {
            type: Boolean,
            default: false
        },
        blogAnswerUrlParams: {
            type: Object,
            default: {}
        }
    },

    data() {
        return {
            mediaBaseUrl: `${process.env.VUE_APP_API_BASE_URL}/skin/frontend/mcp/default/images/`,
        };
    },

    methods: {
        /**
         * handle add or remove overallRenovation question/answer
         * @param {Object} params {isEligible, question}
         */
        handleOverallRenovationQuestion(params) {
            this._pushNextNewQuestions(params.question);
            this.currentQuestion.index++;
            
            this.hideLeadForm();  
        },

        /**
         * set answer validity false
         */
        setAnswerValidity(value) {
            this._isAnswerValid(value);
        },


        /**
         * Pass throught all questions before leadForm
         */
        continueTunnelForm() {
            this._nextStepLeadForm(false);
            this.nextQuestion();
        },

        /**
         * Trigger endForm (leadForm or End simulator)
         */
        triggerEndForm() {
            const isDwellingForm = this.currentForm.name === "dwelling";

            const isEndForm =   this.leadForm.isNextStep && this.currentQuestion.isAnswerValid ||
                                isDwellingForm && this._handleOptionalDewellingQuestions(this.currentQuestion.key, this.currentForm.answers[this.currentQuestion.key]) ||
                                this.currentQuestion.index == this.currentForm.questions.length -1;

            return isEndForm
        },

        triggerErrorMessage() {
            this.displayErrorMessage = true;
            this._scrollToErrorMessage();
        },

        nextQuestion() {
            const hasMoreQuestion   = this.currentQuestion.index < this.currentForm.questions.length - 1;            
            const hasGtmEvents      = this.currentQuestion.gtmDatas.name;

            if(!this._isQuestionAnswered())return this.triggerErrorMessage(); 

            // trigger endForm (leadForm or End simulator)
            let isEndForm = this.triggerEndForm()
            this._handleEndForms(isEndForm); 
            
            //Gtm
            if(hasGtmEvents) this._pushGtmEvents(this.currentQuestion.gtmDatas)

            // NextQuestion
            if(
                hasMoreQuestion &&
                !isEndForm &&
                this.currentQuestion.isAnswerValid
            ) {
                this.setPreviousQuestion({index:this.currentQuestion.index, form:this.currentForm.name})
                this.displayErrorMessage = false; 
                this.currentQuestion.index++; 
            } 
        },

        /**
         * Previous question
         */
        prevQuestion() {
            this.displayErrorMessage = false;
            this.currentQuestion.index--;
        },


        /**
         * Save answer and nextQuestion
         */
        handleAnswer(questionKey, answer, nextQuestion = false) {

            // trigger exitPage failure
            const isSubQuestionParent = questionKey.includes('_parent');
            const isRadioInputType = this.currentForm.questions.find(questionForm => questionForm.key === questionKey)?.input_type == 'radio';

            if(!isSubQuestionParent && isRadioInputType)this._handleExitOptionAnswer(questionKey, answer);

            this._saveAnswer(
                questionKey, 
                answer,
                this.getAnswerLabel(questionKey, answer)
            );

            if(nextQuestion) this.nextQuestion();
        },

        /**
         * Get French label for answers options
         * For Gtm-events value
         */
        getAnswerLabel(questionKey, answerValue) {
            const currentQuestionsDatas         = this.currentForm.questions.find(question => question.key === questionKey);
            const hasQuestionAnswersOptions     = currentQuestionsDatas?.options && currentQuestionsDatas.options.length > 1;
            
            if(hasQuestionAnswersOptions) {
                const answerDatas = currentQuestionsDatas.options.find(option => option.value === answerValue)
                return answerDatas?.label;
            }
        },

        /**
         * back to Tunnel questions from leadForm
         */
        hideLeadForm() {
            this.leadForm.display = false;
            this.leadForm.isNextStep = false;
        },

        setGtmListeners() {
            EventBus.$on('gtm-leadForm::push', ()=> this._pushGtmEvents({name: 'personal_info', step:14}));
            EventBus.$on('gtm-savings::push', ()=> this._pushGtmEvents({name: 'your_savings', step:15}));
            EventBus.$on('gtm-solution::push', ()=> this._pushGtmEvents({name: 'solution', step:16}));
            EventBus.$on('gtm-proposition::push', ()=> this._pushGtmEvents({name: 'proposition', step:17}));
            EventBus.$on('gtm-proposition_rdv::push', ()=> this._pushGtmEvents({name: 'proposition_rdv', step:18}));
            EventBus.$on('gtm-recommendation::push', ()=> this._pushGtmEvents({name: 'recommendation', step:19}));
            EventBus.$on('gtm-recommendation_rdv::push', ()=> this._pushGtmEvents({name: 'recommendation_rdv', step:20}));
            EventBus.$on('gtm-thankYou::push', ()=> this._pushGtmEvents({name: 'thank_you_page', step:21}));
            EventBus.$on('gtm-endJourney::push', ()=> this._pushGtmEvents({name: 'end_journey', step:''}));
            EventBus.$on('simulatorAdvanced-error_ownerConsent', ()=> this._pushGtmEvents({name: 'end_journey', step:'', additonalDatas: {message: 'Malheureusement, vous ne remplissez pas les conditions pour obtenir les aides.'}}));
        },

        removeGtmListener() {
            EventBus.$off('gtm-leadForm::push');
            EventBus.$off('gtm-savings::push');
            EventBus.$off('gtm-solution::push');
            EventBus.$off('gtm-proposition::push');
            EventBus.$off('gtm-proposition_rdv::push');
            EventBus.$off('gtm-recommendation::push');
            EventBus.$off('gtm-recommendation_rdv::push');
            EventBus.$off('gtm-thankYou::push');
            EventBus.$off('gtm-endJourney::push');
            EventBus.$off('simulatorAdvanced-error_ownerConsent');
        },

        /**
         * Handle blog answer url params
         */
        handleBlogAnswerUrlParams() {
            let urlKey = Object.keys(this.blogAnswerUrlParams)[0];

            this.currentForm.questions.forEach((question, index) => {
                if(question.key === urlKey) {
                    this.currentQuestion = {
                        index: index,
                        key: question.key,
                        isAnswerValid: false,
                        gtmDatas: question.gtm_datas
                    }
                }
            });
        }

    },

    created() {
        this._initForm(this.tunnelForm.name);
        this.currentQuestion.index = this.tunnelForm.questionIndex;
        EventBus.$emit('stepAction::current', this.currentForm.name);
        //Remove old listener before set again (avoid listen twice)
        this.removeGtmListener();

        this.handleBlogAnswerUrlParams();
    },
    
    
    mounted() {
        this._setCurrentQuestionData();
        this._isAnswerValid(this._isQuestionAnswered())

        //Set listener after removed old
        this.setGtmListeners();
    },
    
}
</script>

<style lang="scss">
.Mcp-TunnelSimulatorPage {
    margin-top: 30px;
    margin-bottom: 100px;
    padding: 0 28px;
    flex: 1;

    .TunnelSimulatorPage {

        &__content {
            display:flex;

            .ImageBanner-container {
                display: none;
            }

            .FormQuestions-container {
                max-width: 100%;
                flex:1;

                &__wrapper {
                    width: 100%;
                    max-width: 895px;
                    margin-left: auto;

                    @include breakpoint(sm) {
                        padding-left: 28px;
                        padding-right: 28px;
                    }
                }

                &__question-info {
                    display: flex;
                    width: fit-content;
                    padding: 10px 20px;
                    color: $c-blue-simu;
                    background-color: rgba(86, 148, 242, .15);
                    border-radius: 10px;

                    p {
                        flex: 1;
                        margin-left: 10px;
                    }
                }
            }
        }
    }
}




.Mcp-TunnelSimulatorPage {

    // screen 768pxpx
    @include breakpoint(md) {
        margin-bottom: 0;

        .TunnelSimulatorPage__content {
            .FormQuestions-container {
                &__question-info {
                    margin-top: 40px;
                }
            }
        }
    }

    // Screen large 1024px
    @include breakpoint(lg) {

        .TunnelSimulatorPage__content {
            
            .ImageBanner-container {
                display: initial;
                flex:1;
                max-height: calc(100vh - 160px);
                padding-right: 15px;

                img {
                    height: 100%;
                }
            }

            .FormQuestions-container {
                flex-basis:75%;
                max-width:75%;
            }
        }
    }
}
</style>